import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import icons from './shared/Icons';
import Section from './shared/Section';
import SectionHeading from './shared/SectionHeading';
import AnimatedCounter from './shared/AnimatedCounter';

/* ── industry data ── */
const industries = [
  {
    key: 'finance',
    label: 'Finance',
    icon: 'chart',
    pins: [[28, 38], [72, 30], [48, 55], [82, 42]],
    cases: [
      { title: 'Fraud Detection', desc: 'Real-time transaction monitoring flagging anomalies before they cascade.', metric: 99.2, suffix: '%', metricLabel: 'Detection Rate' },
      { title: 'Risk Modeling', desc: 'Predictive credit scoring with scenario-based stress testing.', metric: 60, suffix: '%', metricLabel: 'Fewer False Positives' },
      { title: 'Algo Trading', desc: 'ML-driven strategies that adapt to market micro-structure in real time.', metric: 3.4, suffix: 'x', metricLabel: 'Return Improvement' },
    ],
  },
  {
    key: 'healthcare',
    label: 'Healthcare',
    icon: 'shield',
    pins: [[35, 32], [55, 48], [22, 52], [68, 36]],
    cases: [
      { title: 'Clinical Analytics', desc: 'AI-assisted diagnosis support improving early detection rates.', metric: 45, suffix: '%', metricLabel: 'Better Detection' },
      { title: 'Patient Outcomes', desc: 'Predictive models reducing readmission within 30 days.', metric: 23, suffix: '%', metricLabel: 'Fewer Readmissions' },
      { title: 'Drug Discovery', desc: 'Candidate screening accelerated through molecular simulation.', metric: 8, suffix: 'x', metricLabel: 'Faster Screening' },
    ],
  },
  {
    key: 'retail',
    label: 'Retail',
    icon: 'globe',
    pins: [[40, 34], [62, 50], [28, 46], [78, 28]],
    cases: [
      { title: 'Customer Intelligence', desc: 'Hyper-personalized recommendations driving conversion uplift.', metric: 35, suffix: '%', metricLabel: 'More Conversions' },
      { title: 'Demand Forecasting', desc: 'Predict inventory needs across 10K+ SKUs seasonally.', metric: 40, suffix: '%', metricLabel: 'Less Overstock' },
      { title: 'Price Optimization', desc: 'Dynamic pricing aligned to elasticity curves in real time.', metric: 18, suffix: '%', metricLabel: 'Revenue Lift' },
    ],
  },
  {
    key: 'manufacturing',
    label: 'Manufacturing',
    icon: 'cog',
    pins: [[45, 40], [58, 30], [32, 55], [75, 48]],
    cases: [
      { title: 'Predictive Maintenance', desc: 'IoT-driven failure prediction 72 hours in advance.', metric: 80, suffix: '%', metricLabel: 'Less Downtime' },
      { title: 'Quality Control', desc: 'Computer vision detecting defects on assembly lines at speed.', metric: 97, suffix: '%', metricLabel: 'Defect Catch Rate' },
      { title: 'Supply Chain', desc: 'End-to-end visibility with bottleneck prediction.', metric: 52, suffix: '%', metricLabel: 'Faster Throughput' },
    ],
  },
];

/* ── World map SVG (simplified outline) ── */
const WorldMap = ({ pins }) => (
  <svg viewBox="0 0 100 60" className="absolute inset-0 w-full h-full opacity-[0.07]" fill="none" stroke="currentColor" strokeWidth="0.3">
    {/* Simplified continent outlines */}
    <path d="M15 18 C18 12,28 10,30 16 C32 12,38 14,36 18 C38 22,28 28,22 26 C18 28,14 24,15 18Z" className="stroke-primary/40" />
    <path d="M42 14 C48 8,72 8,78 16 C80 24,74 30,68 28 C62 32,56 30,52 26 C46 24,40 20,42 14Z" className="stroke-primary/40" />
    <path d="M20 32 C24 28,34 30,32 38 C30 44,24 46,20 42 C16 40,18 36,20 32Z" className="stroke-primary/40" />
    <path d="M46 32 C50 28,58 30,56 38 C54 46,48 44,46 40 C44 38,44 36,46 32Z" className="stroke-primary/40" />
    <path d="M70 24 C76 22,86 28,84 36 C82 42,74 40,72 34 C70 30,68 28,70 24Z" className="stroke-primary/40" />
    <path d="M80 44 C86 42,92 48,88 54 C84 56,78 52,80 44Z" className="stroke-primary/40" />
    {/* Animated pins */}
    {pins.map(([cx, cy], i) => (
      <motion.circle
        key={i}
        cx={cx}
        cy={cy}
        r="1.2"
        className="fill-primary"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: [0, 1, 1, 0.5], scale: [0, 1.5, 1, 1] }}
        transition={{ duration: 1.2, delay: i * 0.2, repeat: Infinity, repeatDelay: 3 }}
      />
    ))}
  </svg>
);

/* ── Case study card ── */
const CaseCard = ({ c, index }) => (
  <motion.div
    className="glass-card rounded-xl p-6 group relative overflow-hidden"
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.4, delay: index * 0.1 }}
    whileHover={{ y: -4 }}
  >
    <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-primary/5 -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500" aria-hidden="true" />
    <div className="relative">
      {/* Metric badge */}
      <div className="inline-flex items-baseline gap-1 mb-4 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
        <span className="text-xl font-bold text-secondary">
          <AnimatedCounter target={c.metric} suffix={c.suffix} duration={1500} />
        </span>
        <span className="text-[10px] text-gray-500 uppercase tracking-wider">{c.metricLabel}</span>
      </div>
      <h4 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors">{c.title}</h4>
      <p className="text-gray-400 text-sm leading-relaxed">{c.desc}</p>
    </div>
  </motion.div>
);

/* ── Tab button ── */
const TabButton = ({ ind, active, onClick }) => (
  <button
    onClick={onClick}
    className={`relative px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap flex items-center gap-2 ${
      active ? 'text-black' : 'text-gray-400 hover:text-white'
    }`}
  >
    {active && (
      <motion.div
        layoutId="tab-bg"
        className="absolute inset-0 rounded-lg bg-primary"
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      />
    )}
    <span className="relative z-10 flex items-center gap-2">
      <span className="scale-75">{icons[ind.icon]}</span>
      {ind.label}
    </span>
  </button>
);

/* ── Mobile dropdown ── */
const MobileDropdown = ({ industries: inds, active, setActive }) => (
  <div className="sm:hidden mb-8">
    <select
      value={active}
      onChange={(e) => setActive(Number(e.target.value))}
      className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-primary/50 transition-colors appearance-none"
      style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%2308CB00%27 stroke-width=%272%27%3E%3Cpath d=%27M6 9l6 6 6-6%27/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
    >
      {inds.map((ind, i) => (
        <option key={ind.key} value={i}>{ind.label}</option>
      ))}
    </select>
  </div>
);

/* ── Main ── */
const UseCases = () => {
  const [active, setActive] = useState(0);
  const current = industries[active];

  return (
    <Section id="use-cases" className="py-24 lg:py-32 relative overflow-hidden">
      {/* World map background */}
      <WorldMap pins={current.pins} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <SectionHeading
          tag="Use Cases"
          title={<>Intelligence Across <span className="gradient-text">Every Industry</span></>}
          desc="See how organizations in diverse sectors leverage OpticVance to gain their competitive edge."
        />

        {/* Desktop/Tablet tabs */}
        <div className="hidden sm:flex justify-center gap-2 mb-12">
          {industries.map((ind, i) => (
            <TabButton key={ind.key} ind={ind} active={i === active} onClick={() => setActive(i)} />
          ))}
        </div>

        {/* Mobile dropdown */}
        <MobileDropdown industries={industries} active={active} setActive={setActive} />

        {/* Case study grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={current.key}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {current.cases.map((c, i) => (
              <CaseCard key={c.title} c={c} index={i} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </Section>
  );
};

export default UseCases;
